import React from 'react';
import { Box, Container, Typography, Paper } from '@mui/material';
import Header from './Header';
import Footer from './Footer';

interface MaintenancePageProps {
  message?: string;
}

const MaintenancePage: React.FC<MaintenancePageProps> = ({ 
  message = 'This site is down for maintenance' 
}) => {
  // Simple markdown-like parsing for basic formatting
  const renderMarkdown = (text: string) => {
    // Split by lines and process each line
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    
    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      // Skip empty lines
      if (!trimmedLine) {
        elements.push(<br key={`br-${index}`} />);
        return;
      }
      
      // Handle headers
      if (trimmedLine.startsWith('# ')) {
        elements.push(
          <Typography key={index} variant="h4" component="h1" gutterBottom>
            {trimmedLine.substring(2)}
          </Typography>
        );
      } else if (trimmedLine.startsWith('## ')) {
        elements.push(
          <Typography key={index} variant="h5" component="h2" gutterBottom>
            {trimmedLine.substring(3)}
          </Typography>
        );
      } else if (trimmedLine.startsWith('### ')) {
        elements.push(
          <Typography key={index} variant="h6" component="h3" gutterBottom>
            {trimmedLine.substring(4)}
          </Typography>
        );
      } else {
        // Handle regular text with potential links
        const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        const parts = [];
        let lastIndex = 0;
        let match;
        
        while ((match = linkRegex.exec(trimmedLine)) !== null) {
          // Add text before the link
          if (match.index > lastIndex) {
            parts.push(trimmedLine.substring(lastIndex, match.index));
          }
          
          // Add the link
          parts.push(
            <a 
              key={`link-${index}-${match.index}`}
              href={match[2]} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: '#1976d2', 
                textDecoration: 'none',
                borderBottom: '1px solid #1976d2'
              }}
            >
              {match[1]}
            </a>
          );
          
          lastIndex = match.index + match[0].length;
        }
        
        // Add remaining text after the last link
        if (lastIndex < trimmedLine.length) {
          parts.push(trimmedLine.substring(lastIndex));
        }
        
        // If no links found, just add the text
        if (parts.length === 0) {
          parts.push(trimmedLine);
        }
        
        elements.push(
          <Typography key={index} variant="body1" paragraph>
            {parts}
          </Typography>
        );
      }
    });
    
    return elements;
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      bgcolor: '#f5f7fa'
    }}>
      <Header />
      
      <Container 
        maxWidth="md" 
        sx={{ 
          py: { xs: 4, sm: 6, md: 8 }, 
          px: { xs: 2, sm: 3, md: 3 },
          flex: '1 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Paper 
          elevation={3}
          sx={{ 
            p: { xs: 3, sm: 4, md: 6 },
            textAlign: 'center',
            width: '100%',
            maxWidth: 600
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="h3" 
              component="div" 
              sx={{ 
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                mb: 1,
                color: '#f57c00'
              }}
            >
              ⚠️
            </Typography>
          </Box>
          
          <Box sx={{ 
            '& > *:last-child': { mb: 0 } // Remove margin from last element
          }}>
            {renderMarkdown(message)}
          </Box>
        </Paper>
      </Container>
      
      <Footer />
    </Box>
  );
};

export default MaintenancePage;